// src/pages/messages/SmsReplyRow.jsx
//
// InboxV2 row for a team with unread inbound SMS replies
// (features/unified-replies/02-wireframe.md §3a). Mirrors ChatRow's
// anatomy exactly — same paddings, typography, and unread treatments —
// so the inbox reads as one list. The row exists only while unread
// replies exist (BA D-3), so it always renders in the unread state.
//
// It does exactly one thing: navigate to the team's Messages page
// (BA D-5 / W-4 — no inline actions, read state stays manual there).
//
// Renders zero PII by construction: props carry only teamId, name,
// count, newestAtMs (store strips everything else — security S2).

import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { DevicePhoneMobileIcon } from '@heroicons/react/24/outline'
import { fmtRelative } from './relativeTime'

const HEAD_FONT = "'Barlow Condensed', sans-serif"

export default function SmsReplyRow({ teamId, name, count, newestAtMs }) {
  const { t } = useTranslation()

  return (
    <Link
      to={`/teams/${teamId}/messages`}
      aria-label={`${name}: ${t('messages.smsRepliesHint', 'Text replies from parents')}`}
      className="block px-4 py-3 border-b border-zinc-100 hover:bg-[#f7faf5] transition-colors bg-[#b5f03d]/5"
    >
      <div className="flex items-start gap-3">
        {/* Avatar: lime container + phone icon, same treatment as team channels */}
        <div className="h-12 w-12 rounded-2xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 flex items-center justify-center flex-shrink-0">
          <DevicePhoneMobileIcon className="h-6 w-6 text-[#5a8a1f]" />
        </div>

        <div className="flex-1 min-w-0">
          {/* Top row: team name + SMS pill + time */}
          <div className="flex items-center justify-between gap-2 mb-0.5">
            <p className="text-sm truncate font-bold text-[#0d1f14]">
              {name}
              <span
                className="ml-2 inline-flex items-center gap-0.5 rounded bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/30 text-[#5a8a1f] px-1.5 py-px text-[9px] font-bold uppercase align-middle"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
              >
                SMS
              </span>
            </p>
            <span className="text-[11px] flex-shrink-0 text-[#5a8a1f] font-semibold">
              {fmtRelative(newestAtMs)}
            </span>
          </div>

          {/* Preview: localized unread count + unread dot */}
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs truncate text-[#0d1f14] font-medium">
              {t('messages.smsRepliesRow', { count, defaultValue: '{{count}} new SMS replies' })}
            </p>
            <span
              aria-label="Unread"
              className="h-2 w-2 rounded-full bg-[#b5f03d] flex-shrink-0 ring-2 ring-[#b5f03d]/30"
            />
          </div>
        </div>
      </div>
    </Link>
  )
}
